import { useEffect, useState } from "react";
import { Box, Tooltip, Typography, styled } from "@mui/material";
import BoltIcon from "@mui/icons-material/Bolt";
import {
  isDesignerRole,
  useActiveBalance,
  useCreditStore,
  useCreditWorkspace,
  useWorkspaceContext,
} from "ui";
import { useLoginState } from "@/studioxr/auth/LoginState";
import { CREDIT_THRESHOLDS } from "@/constants/credits";
import {
  useConcepts,
  conceptBelongsToWorkspace,
  getTimestampMs,
  isBusy,
} from "@/hooks/useConcepts";

const CreditBalanceContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(0.75),
  padding: theme.spacing(0.75, 1.5),
  backgroundColor: "#F9FAFB",
  border: "1px solid #E5E7EB",
  borderRadius: theme.spacing(1),
  cursor: "pointer",
  fontSize: "0.875rem",
  fontWeight: 600,
  transition: "all 0.2s ease",
  "&:hover": {
    backgroundColor: "#F3F4F6",
  },
  "&.low-balance": {
    borderColor: "#EF4444",
    backgroundColor: "#FEF2F2",
    "& .credit-icon": {
      color: "#DC2626",
    },
  },
  "&.error-state": {
    borderColor: "#D1D5DB",
    backgroundColor: "#F9FAFB",
    "& .credit-icon": {
      color: "#9CA3AF",
    },
  },
  [theme.breakpoints.down("sm")]: {
    display: "none", // Hide on mobile to save space
  },
}));

interface CreditBalanceDisplayProps {
  uid?: string | null;
  onClick?: () => void;
  size?: "compact" | "normal";
  showLabel?: boolean;
  /** Team workspace only: renders "<name> credits" when it is the shown team. */
  teamFirm?: { id: string; name?: string } | null;
}

export const CreditBalanceDisplay = ({
  uid,
  onClick,
  showLabel = false,
  teamFirm,
}: CreditBalanceDisplayProps) => {
  const profile = useLoginState((state) => state.profile);
  const permissions = useLoginState((state) => state.permissions);
  const resolvedUid = uid ?? profile?.uid ?? null;

  const workspaceCtx = useWorkspaceContext(resolvedUid, permissions);
  // The workspace whose credits we display/spend. Identical to the active
  // workspace when workspaces are enabled; when the feature is gated in
  // production it pins team designers back onto their team wallet (the
  // pre-workspaces behavior) so they don't see/spend their tiny Personal
  // balance instead of the team's. See useCreditWorkspace.
  const activeWorkspace = useCreditWorkspace(resolvedUid, permissions);
  // Inactive roster members (e.g. an inactive designer_and_admin) can't spend
  // team credits, so don't show a balance for them — it reads as spendable.
  const isActiveSpender = workspaceCtx.isActiveMemberOfActiveWorkspace;

  const pendingArtifactCredits = useCreditStore(
    (s) => s.pendingArtifactCredits,
  );
  const subscribeToBalance = useCreditStore((s) => s.subscribeToBalance);
  const subscribeToTeamBalance = useCreditStore(
    (s) => s.subscribeToTeamBalance,
  );
  const fetchTeamBalance = useCreditStore((s) => s.fetchTeamBalance);
  const unsubscribeAll = useCreditStore((s) => s.unsubscribeAll);

  const { balance: effectiveBalance, hideBalance } =
    useActiveBalance(activeWorkspace);

  // Drive the Firestore subscription off the active workspace so that
  // switching Personal <-> Team tears down the old listener and starts the
  // right one. Pre-A.2a this was driven by useTeamMembership and would
  // always pin to the team for a team member regardless of selection.
  useEffect(() => {
    if (workspaceCtx.loading || !resolvedUid) return undefined;
    if (activeWorkspace.kind === "team") {
      // A pure admin (no designer seat) manages the team but cannot spend its
      // credits — don't subscribe/sync the balance for the bar (useActiveBalance
      // hides it anyway). The Team → Credits tab fetches it independently for
      // management. Designers (and designer_and_admin) get the live balance:
      if (isDesignerRole(activeWorkspace.role) && isActiveSpender) {
        subscribeToTeamBalance(activeWorkspace.firmId);
        // The Firestore listener alone won't materialize
        // creditBalances/team_<firmId> — that doc is only written server-side
        // by syncTeamWalletFromStripe, which the GET /wallet endpoint triggers.
        // A freshly-created team has no doc yet, so the listener fires onEmpty
        // and the balance stays hidden. fetchTeamBalance(force) pulls the
        // latest Stripe grants into Firestore so the balance shows in the bar
        // without first visiting the Team → Credits tab. (force=true dedups
        // per-firmId in the store, so concurrent mounts are safe.)
        fetchTeamBalance(activeWorkspace.firmId, true).catch((err) => {
          console.warn("[CreditBalanceDisplay] fetchTeamBalance failed:", err);
        });
      }
    } else {
      subscribeToBalance(activeWorkspace.uid);
    }
    return () => {
      unsubscribeAll();
    };
  }, [
    resolvedUid,
    activeWorkspace.id,
    activeWorkspace.kind,
    activeWorkspace.kind === "team" ? activeWorkspace.role : null,
    isActiveSpender,
    workspaceCtx.loading,
    subscribeToBalance,
    subscribeToTeamBalance,
    fetchTeamBalance,
    unsubscribeAll,
  ]);

  // Soft-deduct credits for in-flight billable operations.
  // Real credit deduction only happens when the version becomes FINISHED,
  // so we subtract PENDING concept versions from the display to avoid stale balance.
  const { concepts } = useConcepts();
  const [pendingFromFirestore, setPendingFromFirestore] = useState(0);

  useEffect(() => {
    const FIVE_MINUTES_MS = 5 * 60 * 1000;
    // Only subtract pending concepts that belong to the active workspace —
    // otherwise switching to Personal mid-team-generation would show a
    // spurious -1 on the personal balance (and vice versa) since the same
    // pending count was being subtracted from whatever balance was shown.
    const count = () => {
      const now = Date.now();
      const pending = concepts.filter(
        (c) =>
          isBusy(c) &&
          // Free generations (the first finalize per remix flow, #3381) are
          // never charged — don't soft-deduct them, or an in-flight free
          // concept shows a phantom -1 that reverts on refresh.
          !c.free_generation &&
          conceptBelongsToWorkspace(c, activeWorkspace) &&
          now -
            getTimestampMs(
              c.current?.created_at ?? c.updated_at ?? c.created_at,
            ) <
            FIVE_MINUTES_MS,
      ).length;
      setPendingFromFirestore(pending);
    };
    count();
    const interval = setInterval(count, 30_000);
    return () => clearInterval(interval);
  }, [concepts, activeWorkspace.kind, activeWorkspace.id]);

  const pendingCredits = pendingFromFirestore + pendingArtifactCredits;

  const hasBalance = effectiveBalance?.totalBalance != null;
  const shouldHide = hideBalance || !hasBalance || !isActiveSpender;

  const handleClick = () => {
    if (onClick) {
      onClick();
    }
  };

  if (shouldHide) {
    return null;
  }

  const actualCredits = effectiveBalance?.totalBalance ?? 0;
  const credits = Math.max(0, actualCredits - pendingCredits);
  const isLowBalance = credits < CREDIT_THRESHOLDS.LOW_BALANCE;

  const pendingSuffix =
    pendingCredits > 0 ? ` (${pendingCredits} pending)` : "";
  const isTeam = activeWorkspace.kind === "team";
  // useDesignFirm keeps the previous firm until a switched-to team loads.
  const teamName =
    activeWorkspace.kind === "team" && teamFirm?.id === activeWorkspace.firmId
      ? teamFirm.name
      : undefined;
  const tooltipTitle = isTeam
    ? `Your team has ${credits} credit${credits !== 1 ? "s" : ""} remaining${pendingSuffix}`
    : `You have ${credits} credit${credits !== 1 ? "s" : ""} remaining${pendingSuffix}`;

  return (
    <Tooltip title={tooltipTitle}>
      <CreditBalanceContainer
        data-testid="credit-balance-display"
        onClick={handleClick}
        className={isLowBalance ? "low-balance" : ""}
      >
        <BoltIcon
          className="credit-icon"
          sx={{
            fontSize: "1.1rem",
            color: isLowBalance ? "#DC2626" : "#F59E0B",
          }}
        />
        <Typography sx={{ fontSize: "inherit", fontWeight: "inherit" }}>
          {credits}
          {showLabel && ` credit${credits !== 1 ? "s" : ""}`}
        </Typography>
        {/* When the bar is short of room only the name column gives way: the
            pill can shrink to "⚡ 256 credits" but never past it. */}
        {teamName && (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "minmax(0, max-content) auto",
              columnGap: 0.75,
              alignItems: "center",
            }}
          >
            <Typography
              noWrap
              sx={{
                fontSize: "inherit",
                fontWeight: "inherit",
                minWidth: 0,
                maxWidth: 180,
              }}
            >
              {teamName}
            </Typography>
            <Typography sx={{ fontSize: "inherit", fontWeight: "inherit" }}>
              {credits === 1 ? "credit" : "credits"}
            </Typography>
          </Box>
        )}
      </CreditBalanceContainer>
    </Tooltip>
  );
};
